import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  ResponsiveContainer, ComposedChart, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, BarChart, Bar, Area, AreaChart, ReferenceLine, Legend,
} from "recharts";
import {
  Coins, TrendingUp, Wallet, Percent, Clock, ChevronDown,
  Info, BadgeDollarSign,
} from "lucide-react";
import { buildInvestorProjection } from "@/lib/investor";

function money(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Number(v);
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}m`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${Math.round(abs).toLocaleString()}`;
}
function num(v) {
  if (v == null || v === "" || Number.isNaN(v)) return "—";
  return Number(v).toLocaleString();
}
function pctFmt(decimal) {
  if (!Number.isFinite(decimal)) return "—";
  return `${(decimal * 100).toFixed(1)}%`;
}

/**
 * Investor ROI panel — reads the current proposal form + investor inputs,
 * computes IRR / NPV / payback + cashflow table & charts.
 */
export default function InvestorROI({ form, setForm }) {
  const [showTable, setShowTable] = useState(false);

  const proj = useMemo(() => buildInvestorProjection(form), [form]);
  const { rows, kpis, totals } = proj;

  const irrOk = Number.isFinite(kpis.irr);
  const irrHealthy = irrOk && kpis.irr >= (form.discount_rate_pct || 10) / 100;

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr]" data-testid="investor-roi">
      {/* Left column: inputs */}
      <section className="rounded-2xl bg-white border border-black/5 p-6 md:p-8 space-y-5">
        <h2 className="font-heading text-lg font-semibold text-forest">Investor inputs</h2>
        <div className="rounded-xl bg-mist/70 border border-black/5 p-4 flex items-start gap-3">
          <Info size={16} className="text-bright mt-0.5"/>
          <p className="text-[12.5px] text-neutral-600 leading-relaxed">
            Enter the capital cost, ongoing maintenance and financial assumptions.
            The IRR, NPV and payback are calculated from the customer-facing proposal
            (supplied kWh, PPA rate, term, escalations) — so any change on the
            <strong> Customer proposal</strong> tab updates this view automatically.
          </p>
        </div>

        <p className="text-[13px] font-semibold text-forest">Project economics</p>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Capital cost (AUD)">
            <Input type="number" value={form.capital_cost ?? ""} onChange={(e) => update("capital_cost", e.target.value)} data-testid="inv-capital" />
          </Field>
          <Field label="Annual maintenance (AUD)">
            <Input type="number" value={form.annual_maintenance_cost ?? ""} onChange={(e) => update("annual_maintenance_cost", e.target.value)} data-testid="inv-maintenance" />
          </Field>
        </div>

        <p className="text-[13px] font-semibold text-forest">Financial assumptions</p>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Maintenance escalation % / yr">
            <Input type="number" step="0.1" value={form.maintenance_escalation_pct ?? 3} onChange={(e) => update("maintenance_escalation_pct", e.target.value)} data-testid="inv-maint-esc" />
          </Field>
          <Field label="Discount rate % (for NPV)">
            <Input type="number" step="0.1" value={form.discount_rate_pct ?? 10} onChange={(e) => update("discount_rate_pct", e.target.value)} data-testid="inv-discount" />
          </Field>
          <Field label="Terminal value at end of term (AUD)">
            <Input type="number" value={form.terminal_value_aud ?? 0} onChange={(e) => update("terminal_value_aud", e.target.value)} data-testid="inv-terminal" />
          </Field>
        </div>

        <div className="rounded-xl bg-white border border-forest/15 p-4" data-testid="inv-driver-summary">
          <p className="text-[12px] uppercase tracking-widest font-semibold text-forest mb-2">Revenue drivers (from Customer proposal)</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[13px]">
            <Driver label="Term" value={form.proposed_term_years ? `${form.proposed_term_years} yrs` : "—"} />
            <Driver label="PPA rate" value={form.proposed_ppa_rate_cents ? `${form.proposed_ppa_rate_cents} c/kWh` : "—"} />
            <Driver label="Supplied kWh / yr" value={num(form.estimated_energy_supplied_kwh)} />
            <Driver label="PPA escalation" value={`${form.escalation_ppa_pct ?? 3}%`} />
          </div>
        </div>
      </section>

      {/* Right column: preview + KPIs + charts */}
      <section className="space-y-4">
        <div className="rounded-2xl bg-forest text-white p-6 md:p-8 relative overflow-hidden grain">
          <div className="relative z-10">
            <div className="flex items-center gap-2 text-bright text-[11px] uppercase tracking-widest font-semibold">
              <BadgeDollarSign size={14}/> Project IRR
            </div>
            <p
              className={`mt-2 font-heading text-5xl font-semibold ${irrHealthy ? "text-bright" : "text-white"}`}
              data-testid="inv-kpi-irr"
            >
              {pctFmt(kpis.irr)}
            </p>
            <p className="mt-1 text-[13px] text-white/60">
              {irrOk
                ? irrHealthy
                  ? `Above your ${form.discount_rate_pct || 10}% target rate`
                  : `Below your ${form.discount_rate_pct || 10}% target rate`
                : "Provide capital + revenue to compute IRR"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Kpi icon={Coins} label={`NPV @ ${form.discount_rate_pct || 10}%`} value={money(kpis.npv)} testId="inv-kpi-npv" />
          <Kpi icon={Clock} label="Payback" value={kpis.payback_year ? `Year ${kpis.payback_year}` : ">term"} testId="inv-kpi-payback" />
          <Kpi icon={TrendingUp} label="Total revenue" value={money(totals.total_revenue)} testId="inv-kpi-revenue" />
          <Kpi icon={Wallet} label="Total maintenance" value={money(totals.total_opex)} testId="inv-kpi-opex" />
          <Kpi icon={BadgeDollarSign} label="Net profit" value={money(totals.net_profit)} testId="inv-kpi-profit" tone="bright" />
          <Kpi icon={Percent} label="ROI multiple" value={Number.isFinite(totals.roi_multiple) ? `${totals.roi_multiple.toFixed(2)}×` : "—"} testId="inv-kpi-roi" />
        </div>

        <div className="rounded-2xl bg-white border border-black/5 p-4">
          <p className="text-[12px] uppercase tracking-widest font-semibold text-forest px-1">Cumulative cash flow</p>
          <div className="h-56 w-full mt-2" data-testid="inv-chart-cumcf">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={[{ year: 0, cum_cf: -Number(form.capital_cost || 0), revenue: 0, opex: 0, net_cf: -Number(form.capital_cost || 0) }, ...rows]}
                         margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                <defs>
                  <linearGradient id="inv-grad-cum" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(140 80% 40%)" stopOpacity={0.4}/>
                    <stop offset="100%" stopColor="hsl(140 80% 40%)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false}/>
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6B7A73" }} tickFormatter={(v) => `Yr ${v}`}/>
                <YAxis tick={{ fontSize: 11, fill: "#6B7A73" }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} width={62}/>
                <Tooltip content={<InvTooltip formatter={money} />} />
                <ReferenceLine y={0} stroke="#B7C4BE" strokeDasharray="4 4"/>
                <Area type="monotone" dataKey="cum_cf" name="Cumulative CF" stroke="hsl(140 80% 32%)" strokeWidth={2.5} fill="url(#inv-grad-cum)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-black/5 p-4">
          <p className="text-[12px] uppercase tracking-widest font-semibold text-forest px-1">Annual revenue vs maintenance</p>
          <div className="h-56 w-full mt-2" data-testid="inv-chart-rev-opex">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false}/>
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6B7A73" }} tickFormatter={(v) => `Yr ${v}`}/>
                <YAxis tick={{ fontSize: 11, fill: "#6B7A73" }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} width={62}/>
                <Tooltip content={<InvTooltip formatter={money} />} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }}/>
                <Bar dataKey="revenue" name="Revenue" fill="hsl(140 80% 40%)" radius={[6, 6, 0, 0]}/>
                <Bar dataKey="opex" name="Maintenance" fill="hsl(150 12% 62%)" radius={[6, 6, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-mist border border-black/5" data-testid="inv-table">
          <button
            type="button"
            onClick={() => setShowTable((s) => !s)}
            data-testid="inv-table-toggle"
            className="w-full flex items-center justify-between p-4"
          >
            <div className="text-left">
              <p className="font-heading font-semibold text-forest text-[14px]">Year-by-year cashflow</p>
              <p className="text-[11.5px] text-neutral-500">Revenue, maintenance, net & cumulative</p>
            </div>
            <ChevronDown size={16} className={`text-forest transition-transform ${showTable ? "rotate-180" : ""}`}/>
          </button>
          {showTable && (
            <div className="overflow-x-auto border-t border-black/5">
              <table className="min-w-[560px] w-full text-[13px]">
                <thead className="bg-white/70 text-[11px] uppercase tracking-widest text-neutral-500">
                  <tr>
                    <Th>Yr</Th><Th>Supplied</Th><Th>PPA c</Th><Th>Revenue</Th><Th>Maint.</Th><Th>Net</Th><Th>Cum</Th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-black/5">
                    <Td className="font-semibold text-forest">0</Td>
                    <Td>—</Td>
                    <Td>—</Td>
                    <Td>—</Td>
                    <Td>—</Td>
                    <Td className="text-red-700 font-semibold">{money(-Number(form.capital_cost || 0))}</Td>
                    <Td className="text-red-700 font-semibold">{money(-Number(form.capital_cost || 0))}</Td>
                  </tr>
                  {rows.map((r) => (
                    <tr key={r.year} className="border-t border-black/5">
                      <Td className="font-semibold text-forest">{r.year}</Td>
                      <Td>{num(r.supplied_kwh)}</Td>
                      <Td>{r.ppa_rate_cents}c</Td>
                      <Td>{money(r.revenue)}</Td>
                      <Td>{money(r.opex)}</Td>
                      <Td className={r.net_cf < 0 ? "text-red-700 font-semibold" : "text-emerald-700 font-semibold"}>{money(r.net_cf)}</Td>
                      <Td className={r.cum_cf < 0 ? "text-red-700 font-semibold" : "text-forest font-semibold"}>{money(r.cum_cf)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[12.5px] font-semibold text-forest">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function Driver({ label, value }) {
  return (
    <div className="rounded-lg bg-mist border border-black/5 p-2.5">
      <p className="text-[10.5px] uppercase tracking-widest text-neutral-500 font-semibold">{label}</p>
      <p className="mt-0.5 font-semibold text-forest text-[13.5px]">{value}</p>
    </div>
  );
}
function Kpi({ icon: Icon, label, value, testId, tone }) {
  const bright = tone === "bright";
  return (
    <div className={`rounded-2xl border p-4 ${bright ? "bg-bright/10 border-bright/30" : "bg-white border-black/5"}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] uppercase tracking-widest font-semibold text-forest/70">{label}</span>
        <Icon size={14} className="text-forest"/>
      </div>
      <p className="mt-1.5 font-heading font-semibold text-forest text-[18px]">{value}</p>
    </div>
  );
}
function InvTooltip({ active, payload, label, formatter }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl border border-black/10 bg-white shadow-lg px-3 py-2 text-[12px] text-forest">
      <p className="font-semibold">Year {label}</p>
      {payload.map((e, i) => (
        <p key={i} className="mt-1 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: e.color }}/>
          <span className="text-neutral-600">{e.name}:</span>
          <span className="font-semibold">{formatter ? formatter(e.value) : e.value}</span>
        </p>
      ))}
    </div>
  );
}
function Th({ children }) { return <th className="text-left px-3 py-2 font-semibold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 ${className}`}>{children}</td>; }
